import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { fetchFromApi } from '@utils/api';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledSkillsSection = styled.section`
  max-width: 900px;

  .skills-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 25px;
    margin-top: 50px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const StyledCategoryCard = styled.div`
  background-color: var(--light-navy);
  border: 1px solid var(--lightest-navy);
  border-radius: var(--border-radius);
  padding: 2rem;
  transition: var(--transition);

  &:hover {
    border-color: var(--green);
    transform: translateY(-5px);
    box-shadow: 0 10px 30px -15px var(--navy-shadow);
  }

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 20px 0;
    font-size: var(--fz-md);
    color: var(--lightest-slate);
    font-family: var(--font-mono);
    text-transform: uppercase;
    letter-spacing: 0.05em;

    svg {
      color: var(--green);
      width: 18px;
      height: 18px;
    }
  }

  .tag-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const StyledSkillTag = styled.span`
  background-color: var(--navy);
  border: 1px solid rgba(100, 255, 218, 0.3);
  color: var(--green);
  font-family: var(--font-mono);
  font-size: var(--fz-xxs);
  padding: 6px 12px;
  border-radius: 4px;
  transition: var(--transition);
  cursor: default;

  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
    border-color: var(--green);
    transform: translateY(-2px);
  }
`;

const CategoryIcon = ({ category }) => {
  switch (category) {
    case 'Frontend':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-layout">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="9" y1="21" x2="9" y2="9"></line>
        </svg>
      );
    case 'Backend':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-server">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
          <line x1="6" y1="6" x2="6.01" y2="6"></line>
          <line x1="6" y1="18" x2="6.01" y2="18"></line>
        </svg>
      );
    case 'Data Science & AI':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-cpu">
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
          <rect x="9" y="9" width="6" height="6"></rect>
          <line x1="9" y1="1" x2="9" y2="4"></line>
          <line x1="15" y1="1" x2="15" y2="4"></line>
          <line x1="9" y1="20" x2="9" y2="23"></line>
          <line x1="15" y1="20" x2="15" y2="23"></line>
          <line x1="20" y1="9" x2="23" y2="9"></line>
          <line x1="20" y1="15" x2="23" y2="15"></line>
          <line x1="1" y1="9" x2="4" y2="9"></line>
          <line x1="1" y1="15" x2="4" y2="15"></line>
        </svg>
      );
    case 'Databases & Tools':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-tool">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
        </svg>
      );
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-code">
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
      );
  }
};

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const defaultSkills = [
    { name: 'React', category: 'Frontend', order: 1 },
    { name: 'Tailwind CSS', category: 'Frontend', order: 2 },
    { name: 'Vite', category: 'Frontend', order: 3 },
    { name: 'HTML5/CSS3', category: 'Frontend', order: 4 },
    { name: 'Golang (Fiber/Gin)', category: 'Backend', order: 1 },
    { name: 'Node.js (Express)', category: 'Backend', order: 2 },
    { name: 'RESTful API', category: 'Backend', order: 3 },
    { name: 'GORM / Hibernate', category: 'Backend', order: 4 },
    { name: 'Python (Pandas/NumPy)', category: 'Data Science & AI', order: 1 },
    { name: 'Machine Learning (XGBoost/Scikit-Learn)', category: 'Data Science & AI', order: 2 },
    { name: 'Deep Learning (YOLOv8/PyTorch)', category: 'Data Science & AI', order: 3 },
    { name: 'Forecasting (ARIMA/SARIMAX)', category: 'Data Science & AI', order: 4 },
    { name: 'PostgreSQL', category: 'Databases & Tools', order: 1 },
    { name: 'MySQL', category: 'Databases & Tools', order: 2 },
    { name: 'Git & GitHub', category: 'Databases & Tools', order: 3 },
    { name: 'Docker', category: 'Databases & Tools', order: 4 },
  ];

  useEffect(() => {
    const loadSkills = async () => {
      const apiSkills = await fetchFromApi('/skills');
      if (apiSkills && apiSkills.length > 0) {
        setSkills(apiSkills);
      }
    };
    loadSkills();

    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const categories = ['Frontend', 'Backend', 'Data Science & AI', 'Databases & Tools'];
  const displaySkills = skills.length > 0 ? skills : defaultSkills;

  const grouped = categories.reduce((acc, cat) => {
    acc[cat] = displaySkills
      .filter(s => s.category === cat)
      .sort((a, b) => a.order - b.order);
    return acc;
  }, {});

  return (
    <StyledSkillsSection id="skills" ref={revealContainer}>
      <h2 className="numbered-heading">Core Skills</h2>

      <div className="skills-grid">
        {categories.map((cat, i) => (
          <StyledCategoryCard key={cat}>
            <h3>
              <CategoryIcon category={cat} />
              <span>{cat}</span>
            </h3>
            <div className="tag-container">
              {grouped[cat] && grouped[cat].map((skill, j) => (
                <StyledSkillTag key={j}>{skill.name}</StyledSkillTag>
              ))}
            </div>
          </StyledCategoryCard>
        ))}
      </div>
    </StyledSkillsSection>
  );
};

export default Skills;
